import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { PermissionGuard } from '@/components/layout';
import { useAbsenceScopeList } from '@/hooks/absence/useAbsenceScopeList';
import { useUI } from '@/hooks/ui';


export const AbsenceScopeList: React.FC = () => {
  const { t: translate } = useTranslation('absence');
  const { openModal, closeAllModals, openSidePanel } = useUI();
  const {
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    filterDefinitions,
    handleFilterChange,
    employeeSelectorProps,
    handleEmployeeIdsChange,
    selectedEmployeeIds,
    data,
    totals,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
  } = useAbsenceScopeList();

  const handleSelectMedicalLeave = useCallback(() => {
    closeAllModals();
    openSidePanel({
      title: translate('medicalLeave.sidePanel.title'),
      component: 'CreateMedicalLeaveForm',
      props: {},
    });
  }, [closeAllModals, openSidePanel, translate]);

  const handleSelectPermits = useCallback(() => {
    closeAllModals();
    openSidePanel({
      title: translate('specialLeave.sidePanel.title'),
      component: 'CreateSpecialLeaveForm',
      props: {},
    });
  }, [closeAllModals, openSidePanel, translate]);

  const handleNuevoAusentismo = useCallback(() => {
    openModal(
      'NewAbsenceModal',
      {
        onSelectMedicalLeave: handleSelectMedicalLeave,
        onSelectPermits: handleSelectPermits,
      },
      true,
    );
  }, [openModal, handleSelectMedicalLeave, handleSelectPermits]);

  return (
    <PageLayout dynamicTitle={translate('scope.title')}>
      <div className="flex flex-col gap-6" data-testid="absence-scope-list-page">
        <div className="flex justify-start">
          <PermissionGuard action="create">
            <PrimaryButton
              onClick={handleNuevoAusentismo}
              iconLeft={<IconScout name="plus" />}
              data-testid="nuevo-ausentismo-button"
            >
              {translate('scope.actions.newAbsence')}
            </PrimaryButton>
          </PermissionGuard>
        </div>

        <div className="flex flex-row gap-4 overflow-x-auto pb-4 px-2">
          <IndicatorCard
            label={translate('indicators.licencias')}
            value={totals.licencias}
            variant="important"
            data-testid="indicator-licencias"
          />
          <IndicatorCard
            label={translate('indicators.vacaciones')}
            value={totals.vacaciones}
            variant="important"
            data-testid="indicator-vacaciones"
          />
          <IndicatorCard
            label={translate('indicators.permisosEspeciales')}
            value={totals.permisosEspeciales}
            variant="important"
            data-testid="indicator-permisos-especiales"
          />
          <IndicatorCard
            label={translate('indicators.permisosAdministrativos')}
            value={totals.permisosAdministrativos}
            variant="important"
            data-testid="indicator-permisos-administrativos"
          />
          <IndicatorCard
            label={translate('indicators.permisosFraccion')}
            value={totals.permisosFraccion}
            variant="important"
            data-testid="indicator-permisos-fraccion"
          />
          <IndicatorCard
            label={translate('indicators.faltas')}
            value={totals.faltas}
            variant="error"
            data-testid="indicator-faltas"
          />
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <DateRangeFilter
            value={{ from: filters.from, to: filters.to }}
            defaultValue={{ from: defaultFrom, to: defaultTo }}
            onChange={(range) => {
              setFilters((prev) => ({ ...prev, from: range.from, to: range.to }));
            }}
            maxRangeDays={62}
            closeOnComplete
          />
          <FilterDropdown
            filters={filterDefinitions}
            values={{
              tipoAusentismo: filters.tipoAusentismo,
              estado: filters.estado,
            }}
            onChangeFilter={handleFilterChange}
            appendFilters={
              <Selector
                label={translate('scope.filters.employee')}
                placeholder={translate('scope.filters.employeePlaceholder')}
                value={selectedEmployeeIds}
                onChange={handleEmployeeIdsChange}
                options={employeeSelectorProps.options}
                onLoadMore={employeeSelectorProps.onLoadMore}
                hasNextPage={employeeSelectorProps.hasNextPage}
                isLoadingMore={employeeSelectorProps.isLoadingMore}
                onSearchChange={employeeSelectorProps.onSearchChange}
                searchable
                multiple
              />
            }
          />
        </div>

        <Table
          columns={columns}
          data={data}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable={true}
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'fechaCreacion', direction: 'desc' }}
          data-testid="table-ausentismos-scope"
        />
      </div>
    </PageLayout>
  );
};

export default AbsenceScopeList;
